import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { fetchCapCompaniesService } from 'services/modules/idp/company';
import { setCompanyData } from 'stores/modules/idp/authreducer';

import {
  Divider,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Paper,
  TablePagination,
  Card,
  CardContent,
  CardHeader,
  Popover,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';

const CapCompanies = () => {
  // State variables
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedCompanies, setExpandedCompanies] = useState({});
  const [expandedAssessments, setExpandedAssessments] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Redux selectors
  const companyIDContainer = useSelector((state) => state.auth.user.company.id);
  const capCompaniesContainer = useSelector((state) => state.company.capCompanies);
  const authenticatedUserContainer = useSelector(state => state.auth.user);

  // Redux dispatch
  const dispatch = useDispatch();

  // React router
  const navigate = useNavigate();

  // useEffect hook to fetch the companies which have CAP service subscribed
  useEffect(() => {
    const params = { company: companyIDContainer, page: page + 1, limit: rowsPerPage };
    if(companyIDContainer!==null)
    dispatch(fetchCapCompaniesService({ params}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyIDContainer, page, rowsPerPage]);

  // Method to toggle the view of partner customer companies under partner company
  const toggleCompany = (companyID) => {
    setExpandedCompanies((prevState) => ({
      ...prevState,
      [companyIDContainer]: !prevState[companyIDContainer],
    }));
  };

  // Method to handle search query like company name,domain
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Handles page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handles change in rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handles navigation to view users component
  const handleViewUsers = async (company) => {
    let obj = {};
    obj.companyid = company.id;
    obj.companyname = company.name;
    obj.domain = company.domain;
    await dispatch(setCompanyData(obj));
    navigate(`/cap/users`);
  };

  // Handles navigation to edit company component
  const handleEditCompany = (companyId) => {
    navigate(`/company-edit/${companyId}`);
  };

  // Handles navigation to company profile component
  const handleViewCompany = async (company) => {
    let obj = {};
    obj.companyid = company.id;
    obj.companyname = company.name;
    obj.domain = company.domain;
    await dispatch(setCompanyData(obj));
    navigate(`/company-profile`);
  };

  // Handles navigation to add company component
  const handleAddCompany = () => {
    navigate(`/add-company`);
  };

  // Handles navigation to historical assessments component
  const viewAssessments =async (company) => {
    let obj = {};
    obj.companyid = company.id;
    obj.companyname = company.name;
    obj.domain = company.domain;
    await dispatch(setCompanyData(obj));
    navigate("/cap/assessments-history");
  };

  // Handles the toggle functionality for closing and opening assessments list dialog
  const toggleAssessment = (event, companyIdContainer, assessmentId) => {
    setAnchorEl(event.currentTarget);

    setExpandedAssessments((prevState) => ({
      ...prevState,
      [`${companyIdContainer}_${assessmentId}`]: !prevState[`${companyIdContainer}_${assessmentId}`],
    }));
  };
  
  if (!capCompaniesContainer || !capCompaniesContainer.data || capCompaniesContainer.data.length === 0) {
    return (
      <div className="loading-spinner-container">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="m-2">
      <div className="row header-cont p-2">
        <div className="col-xs-12 col-sm-6 col-md-6 col-lg-3 partner-cont">
          <img src="../assets/partners.svg" className="card-icon" alt="logo" />
          <span className="icon-color text-h5 text-bold font">Partners</span>
        </div>
        <div className="col-xs-12 col-sm-6 col-md-6 col lg-3 cont">
          <Button variant="contained" className="add-company font" onClick={() => handleAddCompany()}>Add company</Button>
        </div>
      </div>
      <Divider className="divider" />
      <div className="col lg-4 col-md-4 col-sm-12 col-xs-12 search">
        <TextField
          variant="outlined"
          placeholder="Search"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </div>
      <div>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Assessments</TableCell>
                <TableCell>Last assessment</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {capCompaniesContainer.data.map((company) => (
                <React.Fragment key={company.id}>
                  <TableRow>
                    <TableCell className="text-blue text-bold" onClick={() => toggleCompany(company.id)}>
                      {company.name}
                      {expandedCompanies[company.id] ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </TableCell>
                    <TableCell>{company.email}</TableCell>
                    <TableCell>{company.phone}</TableCell>
                    <TableCell>{company.location}</TableCell>
                    <TableCell onClick={(event) => company.assessmentsCount > 0 && toggleAssessment(event, company.id, 'main')}>
                      {company.assessmentsCount >= 0 ? (
                        <>
                          {company.assessmentsCount}
                          {company.assessmentsCount > 0 && (
                            <Popover
                              open={expandedAssessments[`${company.id}_main`]}
                              anchorEl={anchorEl}
                              onClose={()=>setAnchorEl(null)}
                              anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                              }}
                              transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                              }}
                            >
                              <Card>
                                <CardHeader title="Assessments" />
                                <CardContent>
                                  {company.assessments.map((assessment, index) => (
                                    <div key={assessment.standard} className="ass-card-spacing">
                                      {`${index + 1}. ${assessment.standard}: `}
                                      <strong>{`${assessment.score.toFixed(2)}/100`}</strong>
                                    </div>
                                  ))}
                                  <Button variant="contained" onClick={() => viewAssessments(company)} className="ass-btn">View details</Button>
                                </CardContent>
                              </Card>
                            </Popover>
                          )}
                        </>
                      ) : (
                        '0 '
                      )}
                    </TableCell>
                    <TableCell>{company.lastassessment.substring(0, 10)}</TableCell>
                    <TableCell>
                      <Tooltip title="View Users" placement="top">
                        <div className="users-container">
                          <GroupIcon className='usersandediticon' onClick={() => handleViewUsers(company)} />
                        </div>
                      </Tooltip>
                      <Tooltip title="View Company" placement="top">
                        <img src="../assets/viewicon.svg" alt="View" className='icons-style' onClick={() => handleViewCompany(company)} />
                      </Tooltip>
                      {(authenticatedUserContainer &&
                        authenticatedUserContainer.roles &&
                        authenticatedUserContainer.roles.some((role) =>
                          role.permissions.includes('UPDATE_SELF_COMPANY')
                        )
                      ) && (
                        <Tooltip title="Edit Company" placement="top">
                          <div className="edit-container">
                            <EditIcon className='usersandediticon' onClick={() => handleEditCompany(company.id)} />
                          </div>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                  {expandedCompanies[company.id] &&
                    company.customers &&
                    company.customers.filter((subCompany) => {
                      return (
                        subCompany.name.toLowerCase().includes(searchQuery) ||
                        subCompany.location.toLowerCase().includes(searchQuery)
                      );
                    })
                    .map((subCompany) => (
                      <TableRow key={subCompany.id}>
                        <TableCell className="text-blue text-bold">{subCompany.name}</TableCell>
                        <TableCell>{subCompany.email}</TableCell>
                        <TableCell>{subCompany.phone}</TableCell>
                        <TableCell>{subCompany.location}</TableCell>
                        <TableCell onClick={(event) => subCompany.assessmentsCount > 0 && toggleAssessment(event, subCompany.id, 'sub')}>
                          {subCompany.assessmentsCount >= 0 ? (
                            <>
                              <div style={{ cursor: 'pointer' }}>
                                {subCompany.assessmentsCount}
                              </div>
                              {subCompany.assessmentsCount > 0 && (
                                <Popover
                                  open={expandedAssessments[`${subCompany.id}_sub`]}
                                  anchorEl={anchorEl}
                                  onClose={()=>setAnchorEl(null)}
                                  anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                  }}
                                  transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                  }}
                                >
                                  <Card>
                                    <CardHeader title="Assessments" />
                                    <CardContent>
                                      {subCompany.assessments.map((assessment, index) => (
                                        <div key={assessment.standard} className="ass-card-spacing">
                                          {`${index + 1}. ${assessment.standard}`}
                                          <strong>{`${assessment.score.toFixed(2)}/100`}</strong>
                                        </div>
                                      ))}
                                      <Button variant="contained" onClick={() => viewAssessments(subCompany)} className="ass-btn">View details</Button>
                                    </CardContent>
                                  </Card>
                                </Popover>
                              )}
                            </>
                          ) : (
                            '0 Assessments'
                          )}
                        </TableCell>
                        <TableCell>
                          {subCompany.lastassessment.substring(0, 10)}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Users" placement="top">
                            <div className="users-container">
                              <GroupIcon className='usersandediticon' onClick={() => handleViewUsers(subCompany)} />
                            </div>
                          </Tooltip>
                          <Tooltip title="View Company" placement="top">
                            <img src="../assets/viewicon.svg" alt="View" className='icons-style' onClick={() => handleViewCompany(subCompany)} />
                          </Tooltip>
                          {(authenticatedUserContainer &&
                            authenticatedUserContainer.roles &&
                            authenticatedUserContainer.roles.some((role) =>
                              role.permissions.includes('UPDATE_SELF_COMPANY')
                            )
                          ) && (
                            <Tooltip title="Edit Company" placement="top">
                              <div className="edit-container">
                                <EditIcon className='usersandediticon' onClick={() => handleEditCompany(subCompany.id)} />
                              </div>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 7, 10, 15, 20, 25, 50]}
          component="div"
          count={capCompaniesContainer.data.reduce(
            (count, company) =>
              count + company.customers.length + 1,
            0
          )}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </div>

    </div>
  );
};

export default CapCompanies;
